import type { Messages } from './index';

export const en: Messages = {
  // Common
  'common.done': 'Done',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.untitled': 'Untitled',
  'common.ok': 'OK',
  'common.save': 'Save',

  // Toolbar
  'toolbar.toggleSidebar': 'Toggle sidebar (Cmd/Ctrl+\\)',
  'toolbar.toggleOutline': 'Toggle outline (table of contents)',
  'toolbar.docTitle': 'Document title',
  'toolbar.source': 'Source',
  'toolbar.preview': 'Preview',
  'toolbar.pane': 'Pane',
  'toolbar.split': 'Split',
  'toolbar.switch': 'Switch',
  'split.resize': 'Resize the editor/preview split — arrow keys, or double-click for 50/50',
  'toolbar.toggleView': 'Toggle view mode (Cmd/Ctrl+Shift+V)',
  'toolbar.vim': 'Vim',
  'toolbar.toggleVim': 'Toggle Vim mode',
  'toolbar.cycleTheme': 'Cycle theme',
  'toolbar.export': 'Export',
  'toolbar.share': 'Share',
  'toolbar.sharing': 'Sharing…',
  'toolbar.shareTitle': 'Create a read-only share link',
  'toolbar.shareOptions': 'Share options (access + expiry)',
  'toolbar.settings': 'Settings',
  'toolbar.palette': 'Command palette (Cmd/Ctrl+K)',
  'toolbar.more': 'More',

  // Save state
  'save.saved': 'Saved locally',
  'save.saving': 'Saving…',
  'save.offline': 'Offline',

  // Theme labels
  'theme.auto': 'Auto',
  'theme.light': 'Light',
  'theme.dark': 'Dark',

  // Export formats
  'export.failed': 'Export failed',
  'export.md': 'Markdown (.md)',
  'export.html': 'HTML',
  'export.pdf': 'PDF',
  'export.docx': 'Word (DOCX)',
  'export.slides': 'Slides (HTML)',

  // Sidebar
  'sidebar.search': 'Search title & text…',
  'sidebar.searchAria': 'Search documents by title and content',
  'sidebar.import': 'Import Markdown file(s)',
  'sidebar.new': 'New document (Cmd/Ctrl+N)',
  'sidebar.newMore': 'More ways to add a document',
  'sidebar.newTemplate': 'New from template…',
  'sidebar.upload': 'Upload file(s)…',
  'sidebar.importUrl': 'Import from URL…',

  // Tags
  'tags.title': 'Edit tags',
  'tags.edit': 'Edit tags',
  'tags.none': 'No tags yet.',
  'tags.placeholder': 'Add a tag and press Enter',
  'tags.remove': 'Remove tag {tag}',
  'tags.all': 'All',
  'tags.filterAria': 'Filter documents by tag',
  'cmd.doc.tags': 'Edit tags…',
  'cmd.doc.rename': 'Rename document',
  'cmd.doc.move': 'Move to folder…',

  // Import from URL
  'import.zip.invalid': 'Could not open {name}: it is not a readable ZIP archive.',
  'import.zip.too_large': 'Could not open {name}: it is too large to import.',
  'import.zip.unsupported':
    'Could not open {name}: it is encrypted or uses unsupported compression.',
  'import.title': 'Import Markdown',
  'import.hint':
    'Add Markdown, text, Word or HTML files to your library. One document per file; Word and HTML are converted to Markdown on your device. A Notion export (.zip) or an Evernote export (.enex) comes in as a set of notes.',
  'import.drop': 'Drag files here',
  'import.browse': 'or click to choose files',
  'import.formats':
    'Accepts .md, .markdown, .mdown, .mkd, .mdwn, .txt, .docx, .html and .enex — or a .zip of them, folders included',
  'import.importing': 'Importing…',
  'import.skipped': 'Not imported, because these are not text, Word or HTML files: {files}',
  'import.failedTitle': 'Some files could not be converted',
  'import.failed': 'Could not convert, possibly damaged: {files}',
  'import.overlay': 'Drop Markdown files to import',
  'importUrl.title': 'Import from URL',
  'importUrl.hint': 'Paste a link to a Markdown or text document.',
  'importUrl.import': 'Import',
  'importUrl.importing': 'Importing…',
  'importUrl.failed': 'Import failed.',
  'cmd.doc.importUrl': 'Import from URL…',
  'sidebar.pin': 'Pin to top',
  'sidebar.unpin': 'Unpin',
  'sidebar.rename': 'Rename',
  'sidebar.more': 'More actions',
  'sidebar.duplicate': 'Duplicate',
  'sidebar.delete': 'Delete',
  'sidebar.emptyNoMatch': 'No documents match your search.',
  'sidebar.empty': 'No documents yet.',
  'sidebar.loading': 'Loading your library…',
  'tab.close': 'Close tab',
  'tab.closeOthers': 'Close other tabs',
  'folder.move': 'Move to folder',
  'folder.root': 'Root (no folder)',
  'folder.new': 'New folder',
  'folder.newPlaceholder': 'New folder (use / for nesting)…',
  'folder.create': 'Move here',
  'folder.rename': 'Rename folder',
  'folder.renamePrompt': 'Rename folder to:',
  'folder.dropToRoot': 'Drop here to move out of the folder',
  'sidebar.resize': 'Resize sidebar — arrow keys, or double-click to reset',
  'sidebar.sort': 'Sort',
  'sidebar.sortUpdated': 'Last modified',
  'sidebar.sortCreated': 'Date created',
  'sidebar.sortTitle': 'Title (A–Z)',
  'sidebar.sortManual': 'Manual (drag to reorder)',
  'sidebar.moveUp': 'Move up',
  'sidebar.moveDown': 'Move down',
  'sidebar.trash': 'Trash…',

  // Status bar
  'status.words': '{n} words',
  'status.chars': '{n} chars',
  'status.lines': '{n} lines',
  'status.reading': '{n} min read',

  // Settings
  'settings.title': 'Settings',
  'settings.theme': 'Theme',
  'settings.fontSize': 'Editor font size',
  'settings.decrease': 'Decrease font size',
  'settings.increase': 'Increase font size',
  'settings.lineNumbers': 'Line numbers',
  'settings.vim': 'Vim mode',
  'settings.typewriter': 'Typewriter scrolling',
  'settings.exportTheme': 'Export theme (HTML/PDF)',
  'settings.language': 'Language',
  'lang.auto': 'System',
  'lang.en': 'English',
  'lang.de': 'Deutsch',

  // Outline
  'outline.title': 'Outline',
  'outline.close': 'Close outline',
  'outline.empty': 'No headings yet. Add a “# Heading”.',
  'outline.backlinks': 'Linked references',
  'outline.noBacklinks': 'No documents link here yet. Use [[title]] to link.',

  // Focus mode
  'focus.exit': 'Exit focus',
  'focus.exitTitle': 'Exit focus mode (Esc)',

  // Crash screen
  'crash.title': 'Something went wrong',
  'crash.body':
    'markbird hit an unexpected error. Your documents are safe on this device — reloading usually fixes it.',
  'crash.reload': 'Reload markbird',
  'crash.continue': 'Try to continue',

  // Share dialog
  'share.readTitle': 'Read-only link created',
  'share.editTitle': 'Editable link created',
  'share.copiedRead':
    'Copied to your clipboard. Anyone with this link can view a snapshot of this document. Sharing copies the content to EU servers.',
  'share.copiedEdit':
    'Copied to your clipboard. Anyone with this link can edit this document’s shared copy. Manage or revoke it anytime from your shares. Sharing copies the content to EU servers.',
  'share.send': 'Share…',
  'share.sheetTitle': 'A note from markbird',
  'share.openLink': 'Open link',
  'share.failTitle': 'Couldn’t create link',
  'share.empty': 'Nothing to share — the document is empty.',

  // Share options dialog
  'shareOpt.title': 'Share options',
  'shareOpt.access': 'Access',
  'shareOpt.read': 'Read-only',
  'shareOpt.edit': 'Editable',
  'shareOpt.editSignin': 'Editable (sign in required)',
  'shareOpt.expires': 'Expires',
  'shareOpt.never': 'Never',
  'shareOpt.1h': '1 hour',
  'shareOpt.1d': '1 day',
  'shareOpt.7d': '7 days',
  'shareOpt.30d': '30 days',
  'shareOpt.password': 'Password',
  'shareOpt.passwordPlaceholder': 'Optional — leave blank for none',
  'shareOpt.slug': 'Custom link',
  'shareOpt.slugPlaceholder': 'e.g. launch-notes (optional) → /p/…',
  'shareOpt.create': 'Create link',
  'shareOpt.creating': 'Creating…',
  'shareOpt.explainer':
    'Creating a link uploads a copy of this document to markbird’s servers so recipients can open it. Anyone with the link can view it; a password restricts who can open it. You can set an expiry or revoke the link anytime.',

  // Share manager
  'shareMgr.title': 'Your shared links',
  'shareMgr.explainer':
    'These links each point to a copy of a document stored on markbird’s servers. Anyone with a link can open it until it expires or you revoke it.',
  'shareMgr.setExpiry': 'Change expiry…',
  'shareMgr.signin': 'Sign in to manage your shares.',
  'shareMgr.loadFail': 'Couldn’t load shares.',
  'shareMgr.empty': 'No active shares. Use “Share” to create a read-only or editable link.',
  'shareMgr.badgeEdit': 'Editable',
  'shareMgr.badgeRead': 'Read-only',
  'shareMgr.isSite': 'Site',
  'shareMgr.expires': 'expires {date}',
  'shareMgr.copyLink': 'Copy link',
  'shareMgr.revoke': 'Revoke',
  'shareMgr.revoking': 'Revoking…',
  'shareMgr.protected': 'Password-protected',

  // Trash
  'trash.title': 'Trash',
  'trash.note': 'Deleted documents are kept here until you restore or permanently delete them.',
  'trash.empty': 'Trash is empty.',
  'trash.restore': 'Restore',
  'trash.deleteForever': 'Delete forever',
  'trash.deleteConfirm': 'Permanently delete “{title}”? This can’t be undone.',
  'trash.emptyAll': 'Empty trash',
  'trash.emptyConfirm': 'Permanently delete all documents in the trash? This can’t be undone.',
  'trash.deletedAt': 'deleted {date}',

  // Templates
  'tpl.title': 'New from template',
  'tpl.note': 'Start a document from a ready-made structure.',
  'tpl.blank': 'Blank document',
  'tpl.daily': 'Daily note',
  'tpl.meeting': 'Meeting notes',
  'tpl.blog': 'Blog post',
  'tpl.todo': 'To-do list',
  'tpl.readme': 'Project README',

  // Backups
  'cmd.site.publish': 'Publish a folder as a site…',
  'cmd.folderSync.now': 'Sync files now',
  'cmd.appearance.customCss': 'Edit my CSS…',
  'cmd.appearance.disableCustomCss': 'Turn off my CSS',
  'query.empty': 'No notes match this query.',
  'query.count': '{n} note(s)',
  'query.problems': 'Not understood: {what}',
  'query.emptyTasks': 'No tasks match this query.',
  'query.countTasks': '{n} task(s)',
  'query.toggleTask': 'Done: {text}',
  'query.sortBy': 'Sort by {field}',
  'query.editField': '{field} of {title}',
  'query.field.title': 'Title',
  'query.field.updated': 'Updated',
  'query.field.created': 'Created',
  'query.field.folder': 'Folder',
  'query.field.tags': 'Tags',
  'query.field.size': 'Characters',
  'css.title': 'Your own CSS',
  'css.hint':
    'Snippets that restyle the app, including the preview. Nothing is fetched: @import and links to other servers are removed, so a snippet from the internet cannot report back. Exports keep their own look, so a document reads the same for whoever you send it to.',
  'css.add': 'New snippet',
  'css.name': 'Snippet name',
  'css.untitled': 'Snippet {n}',
  'css.enable': 'Use “{name}”',
  'css.delete': 'Delete “{name}”',
  'css.editorLabel': 'CSS for “{name}”',
  'css.placeholder': '.markbird-preview h1 { color: rebeccapurple; }',
  'css.removed': 'Removed, because it would fetch from another server: {what}',
  'css.escape':
    'If your CSS makes the app unusable, open it with ?nocss=1 or run “Turn off my CSS” from the command palette.',
  'css.offTitle': 'Your CSS is off',
  'css.offBody':
    'Switched off {n} snippet(s). They are still in Settings, ready to switch back on.',
  'css.offNone': 'Nothing was switched on.',
  'storage.title': 'Where your notes are kept',
  'storage.browser':
    'Your library lives in this browser. Exports, backups and folder sync are what take it off this device.',
  'storage.native': 'Your notes are files in {place}, on this device.',
  'storage.persisted': 'This browser has promised to keep them.',
  'storage.evictable':
    'This browser may delete them if the device runs low on space. Ask it not to:',
  'storage.unknown': 'This browser does not say whether it may delete them.',
  'storage.keep': 'Ask to keep my library',
  'storage.used': '{used} used of about {quota}',
  'folderSync.title': 'Sync with a folder',
  'folderSync.hint':
    'Keep your library and a folder of .md files on this computer in step, in both directions. Edit notes here or in any other app; changes made on one side appear on the other.',
  'folderSync.noFolder': 'Not syncing with a folder.',
  'folderSync.folderIs': 'Syncing with “{name}”.',
  'folderSync.choose': 'Choose folder…',
  'folderSync.change': 'Choose another folder…',
  'folderSync.now': 'Sync now',
  'folderSync.grant': 'Allow access and sync',
  'folderSync.stop': 'Stop syncing',
  'folderSync.off': 'Off.',
  'folderSync.pending': 'Waiting for the first sync.',
  'folderSync.last': 'Last synced {date}',
  'folderSync.fromDisk': '{n} from the folder',
  'folderSync.toDisk': '{n} to the folder',
  'folderSync.moved': '{n} moved',
  'folderSync.trashed': '{n} moved to a trash',
  'folderSync.permission':
    'The browser needs your permission again to use the folder. Click “Allow access and sync”.',
  'folderSync.error': 'The last sync could not finish. Nothing was lost; it will try again.',
  'folderSync.unsupported':
    'Syncing with a folder needs Chrome, Edge or another Chromium browser on a desktop computer.',
  'folderSync.confirmTitle': 'Sync your library with a folder?',
  'folderSync.confirmBody':
    'Your documents will be written into the folder as .md files, and .md files already in it will be added to your library. From then on, changes on either side are copied to the other. Deleted notes go to the trash here, or to a .markbird-trash folder there. If a note changes on both sides at once, the newer version wins and the other stays in version history.',
  'folderSync.conflicts':
    'Changed both here and in the folder: {titles}. The older version is in history.',
  'github.title': 'Sync with a GitHub repository',
  'github.hint':
    'Keep your library in step with a repository of .md files — in any browser, on any device, with no markbird account. The repository is the storage you already have, and its history is your version history.',
  'github.repo': 'Repository',
  'github.branch': 'Branch',
  'github.path': 'Folder in the repository (optional)',
  'github.token': 'Access token',
  'github.tokenHint':
    'Create a fine-grained personal access token on GitHub with read and write access to this one repository’s contents. It is stored on this device only: it is never synced, exported or included in a backup, and it is sent to api.github.com and nowhere else.',
  'github.connect': 'Connect',
  'github.connectedTo': 'Syncing with {name}.',
  'github.disconnect': 'Disconnect',
  'github.notConnected': 'Not connected.',
  'github.reason.auth':
    'The token was refused. Check that it has contents read and write on this repository.',
  'github.reason.repo': 'No repository of that name, or the token cannot see it.',
  'github.reason.branch': 'The repository is there, but not that branch.',
  'github.reason.error': 'GitHub could not be reached. Nothing was changed.',
  'github.confirmTitle': 'Sync your library with a repository?',
  'github.confirmBody':
    'Your documents will be committed to {repo} as .md files, and .md files already there will be added to your library. From then on, changes on either side are copied to the other. Deleted notes go to the trash here, or to a .markbird-trash folder in the repository. If a note changes on both sides at once, the newer version wins and the other stays in version history.',
  'publish.title': 'Publish a folder as a site',
  'publish.hint':
    'Every note in a folder becomes a page of one small website, with a list of its pages and links between them. What is published is a copy: later edits here do not change it, and publishing again makes a new site.',
  'publish.folder': 'Folder',
  'publish.name': 'Name of the site',
  'publish.address': 'Address',
  'publish.preview': 'It will be at {url}',
  'publish.pages': '{n} pages will be published.',
  'publish.noFolders': 'You have no folders yet. Put the notes you want to publish in one first.',
  'publish.publish': 'Publish',
  'publish.publishing': 'Publishing…',
  'publish.empty': 'That folder has no notes with anything in them.',
  'publish.done': 'Published — {n} pages, one link.',
  'publish.takeDown':
    'The link that takes the whole site down again is kept on this device, in your list of links. Without an account it is the only way back.',
  'publish.error.slug_taken': 'That address is already taken. Try another.',
  'publish.error.too_large': 'That is more than one site can hold. Publish a smaller folder.',
  'publish.error.rate_limited': 'Too many links from here just now. Try again shortly.',
  'publish.error.failed': 'Publishing did not work. Nothing was published.',
  'site.pages': 'Pages',
  'site.expired': 'This site has expired.',
  'site.deleted': 'The site has been taken down.',
  'site.deleteTitle': 'Take this site down',
  'site.deleteHint': 'You opened the link that takes this site down. It removes every page.',
  'site.deleteNow': 'Take the site down',
  'backup.title': 'Local backups',
  'backup.noFolder': 'No backup folder chosen.',
  'backup.folderIs': 'Backing up to “{name}”.',
  'backup.choose': 'Choose folder…',
  'backup.cadence': 'Automatic backup',
  'backup.off': 'Off',
  'backup.change': 'On every change',
  'backup.daily': 'Once a day',
  'backup.now': 'Back up now',
  'backup.last': 'Last backup {date}',
  'backup.never': 'Never backed up',
  'backup.zip': 'Download backup (.zip)',
  'backup.unsupported':
    'Folder backups need a Chromium browser on desktop. You can still download a .zip backup.',
  'backup.done': 'Backed up {n} document(s).',
  'backup.failed': 'Backup failed.',
  'backup.permission': 'Folder permission was denied.',

  // Account dialog
  'account.title': 'Account & data',
  'account.exportH': 'Export',
  'account.exportBody': 'Download every document in your library as a .zip of .md files.',
  'account.exportBtn': 'Export all documents (.zip)',
  'account.exporting': 'Exporting…',
  'account.exported': 'Exported {n} documents as a .zip.',
  'account.deleteH': 'Delete account',
  'account.deleteBody':
    'Permanently deletes your account and all documents synced to the cloud. Copies on this device are kept (as local-only). This cannot be undone.',
  'account.deleteBtn': 'Delete account…',
  'account.deleteConfirm': 'Yes, delete permanently',
  'account.deleting': 'Deleting…',
  'account.deleted': 'Account deleted. Your local documents are kept on this device.',
  'account.reauth': 'For security, please sign in again, then retry deleting your account.',
  'account.deleteFail': 'Couldn’t delete the account: {msg}',
  'account.sharesDeleteFail':
    'your shared links could not be removed, so nothing was deleted. Please try again.',

  // Shortcuts
  'shortcuts.title': 'Keyboard shortcuts',
  'shortcuts.general': 'General',
  'shortcuts.view': 'View',
  'shortcuts.formatting': 'Formatting',
  'sc.palette': 'Command palette',
  'sc.sheet': 'This shortcuts sheet',
  'sc.new': 'New document',
  'sc.find': 'Find & replace',
  'sc.sidebar': 'Toggle sidebar',
  'sc.viewmode': 'Toggle split / switch',
  'sc.pane': 'Switch active pane',
  'sc.focus': 'Exit focus mode',
  'sc.bold': 'Bold',
  'sc.italic': 'Italic',
  'sc.code': 'Inline code',
  'sc.slash': 'Slash snippet menu',
  'sc.leaveEditor': 'Move focus out of the editor',
  'sc.tableCells': 'Next / previous table cell (aligns the table)',
  'sc.today': "Open today's note",
  'sc.quickOpen': 'Open a document',

  // Auth dialog
  'auth.title': 'Sign in',
  'auth.dialogTitle': 'Sign in to sync',
  'auth.google': 'Continue with Google',
  'auth.github': 'Continue with GitHub',
  'auth.apple': 'Continue with Apple',
  'auth.appleUnavailable':
    'Sign in with Apple is not switched on for this build yet. Use another way to sign in for now.',
  'auth.emailLabel': 'Email address',
  'auth.emailPlaceholder': 'you@example.com',
  'auth.sendLink': 'Email me a link',
  'auth.or': 'or',
  'auth.checkInbox': 'Check {email} for a sign-in link, then open it on this device.',
  'auth.signinFail': 'Sign-in failed.',
  'auth.linkFail': 'Could not send the link.',
  'auth.confirmEmail': 'Confirm your email to finish signing in',
  'auth.optional':
    'Optional — markbird works fully offline without an account. Sign in to sync your documents across devices. Your notes stay yours.',

  // Account button
  'acct.signIn': 'Sign in',
  'acct.signInTitle': 'Sign in to sync',
  'acct.account': 'Account',
  'acct.manageShares': 'Manage shared links…',
  'acct.accountData': 'Account & data…',
  'acct.signOut': 'Sign out',
  'sync.off': 'Local only',
  'sync.connecting': 'Connecting…',
  'sync.synced': 'Synced',
  'sync.error': 'Sync error',
  'sync.offline': 'Offline — changes queued',
  'encrypt.title': 'Encryption for synced notes',
  'encrypt.hint':
    'With a passphrase, your notes are encrypted on this device before they are synced, and markbird’s server stores only ciphertext. It can still see how many notes an account has and when each changed — that is what makes syncing work at all.',
  'encrypt.signedOut': 'This is about sync. Signed out, your notes never leave this device.',
  'encrypt.stateOff': 'Synced notes are stored as text.',
  'encrypt.stateOn': 'Synced notes are encrypted, and this device can read them.',
  'encrypt.stateLocked': 'Synced notes are encrypted. This device needs the passphrase.',
  'encrypt.turnOn': 'Encrypt my synced notes',
  'encrypt.turnOff': 'Stop encrypting',
  'encrypt.confirmTitle': 'Encrypt your synced notes?',
  'encrypt.confirmBody':
    'Choose a passphrase. It never leaves your devices, so nobody — including us — can recover it, or the notes in the cloud, if you lose it. The notes on this device stay readable either way. Every other device will ask for the passphrase once.',
  'encrypt.choose': 'Choose a passphrase',
  'encrypt.repeat': 'Type it again',
  'encrypt.mismatchTitle': 'The two did not match',
  'encrypt.mismatchBody': 'Nothing was changed. Try again when you are ready.',
  'encrypt.existsTitle': 'This account is already encrypted',
  'encrypt.existsBody': 'Unlock this device with the passphrase you chose instead.',
  'encrypt.unlock': 'Passphrase for your synced notes',
  'encrypt.unlockGo': 'Unlock',
  'encrypt.wrongTitle': 'That passphrase does not fit',
  'encrypt.wrongBody': 'Nothing was changed. Your notes on this device are untouched.',
  'encrypt.offTitle': 'Stop encrypting?',
  'encrypt.offBody':
    'Your notes will be synced as text again, and every copy already in the cloud is replaced with a readable one.',
  'encrypt.lockedToast': 'Your synced notes are encrypted. This device needs the passphrase.',
  'sync.updated': 'This document was updated on another device.',
  'sync.conflict': 'This document was edited on another device. Your previous version is saved.',
  'sync.review': 'Review previous version',

  // PWA prompt
  'pwa.installTitle': 'Install markbird',
  'pwa.installBody': 'Install markbird as an app.',
  'pwa.install': 'Install',
  'pwa.updateBody': 'A new version of markbird is available.',
  'pwa.reload': 'Reload',
  'pwa.dismiss': 'Dismiss',

  // Undo toast
  'undo.deleted': 'Moved “{title}” to trash.',
  'undo.undo': 'Undo',

  // Share page (read-only viewer / editable)
  'sp.readonly': 'Read-only',
  'sp.editable': 'Editable',
  'sp.save': 'Save',
  'sp.saving': 'Saving…',
  'sp.saved': 'Saved',
  'sp.retry': 'Retry save',
  'sp.copyLib': 'Copy to my library',
  'sp.open': 'Open markbird',
  'sp.loading': 'Loading…',
  'report.open': 'Report this document',
  'report.title': 'Report this document',
  'report.intro': 'Tell us what is wrong with this shared document. You do not need an account.',
  'report.reasonLegend': 'Reason',
  'report.reason.illegal': 'It is illegal',
  'report.reason.copyright': 'It infringes copyright',
  'report.reason.personal_data': 'It contains my personal data',
  'report.reason.abuse': 'Harassment, spam or abuse',
  'report.reason.other': 'Something else',
  'report.details': 'Details (optional)',
  'report.contact': 'Your email (optional)',
  'report.contactHint': 'Only used if we need to follow up with you about this report.',
  'report.send': 'Send report',
  'report.sending': 'Sending…',
  'report.failed': 'The report could not be sent. Please try again.',
  'report.sentTitle': 'Report received',
  'report.sentBody':
    'Thank you. We will review this document and remove it if it breaks the rules.',
  'admin.title': 'Reports',
  'admin.loading': 'Loading reports…',
  'admin.signedOut': 'Sign in to review reports.',
  'admin.forbidden': 'This account cannot review reports.',
  'admin.error': 'Something went wrong. Reload to try again.',
  'admin.empty': 'No open reports.',
  'admin.reported': 'Reported',
  'admin.document': 'Document',
  'admin.alreadyGone': 'Already removed',
  'admin.contact': 'Contact',
  'admin.remove': 'Remove share',
  'admin.confirmRemove': 'Confirm: delete permanently',
  'admin.dismiss': 'Dismiss',
  'editor.aria': 'Markdown source',
  'preview.aria': 'Rendered preview',
  'periodic.heading': 'Daily, weekly and monthly notes',
  'periodic.hint':
    'YYYY year · MM month · DD day · dddd weekday · MMMM month name · GGGG-[W]ww ISO week. Text in [brackets] is kept as is.',
  'periodic.daily': 'Daily notes',
  'periodic.weekly': 'Weekly notes',
  'periodic.monthly': 'Monthly notes',
  'periodic.pattern': 'Title pattern',
  'periodic.folder': 'Folder',
  'periodic.root': 'Library root',
  'periodic.preview': 'Today: {title}',
  'periodic.problem.empty': 'Enter a pattern.',
  'periodic.problem.not-unique':
    'This pattern would give different periods the same title, so they would share one note.',
  'periodic.problem.inconsistent':
    'This pattern would name the same period differently on different days — use GGGG, not YYYY, with weeks.',
  'cmd.doc.today': "Open today's note",
  'cmd.doc.thisWeek': "Open this week's note",
  'cmd.view.vaultMode': 'Toggle vault mode (knowledge base)',
  'cmd.app.help': 'Help & support',
  'cmd.doc.template': 'New from template…',
  'cmd.doc.trash': 'Trash (restore deleted)…',
  'cmd.backup.now': 'Back up library to folder',
  'cmd.backup.zip': 'Download library backup (.zip)',
  'cmd.backup.folder': 'Choose backup folder…',
  'cmd.view.vault.tags': 'Vault: tags',
  'cmd.view.vault.links': 'Vault: unresolved links & orphans',
  'cmd.view.vault.graph': 'Vault: graph',
  'cmd.view.vault.journal': 'Vault: journal calendar',
  'cmd.doc.thisMonth': "Open this month's note",
  'goal.promptTitle': 'Word goal for this document (0 removes it)',
  'goal.set': 'Set goal',
  'goal.invalidTitle': 'That is not a word goal',
  'goal.invalidBody': 'Enter a whole number of words, such as 1000. Enter 0 to remove the goal.',
  'status.wordsOfGoal': '{n} / {goal} words',
  'cmd.doc.wordGoal': 'Set word goal…',
  'settings.pageSize': 'PDF page size',
  'settings.pageSize.auto': 'Choose when printing',
  'settings.pageSize.A4': 'A4',
  'settings.pageSize.Letter': 'US Letter',
  'settings.margins': 'PDF margins',
  'settings.margins.narrow': 'Narrow',
  'settings.margins.normal': 'Normal',
  'settings.margins.wide': 'Wide',
  'settings.exportToc': 'Start PDFs with a table of contents',
  'export.toc': 'Contents',
  'mermaid.offlineType':
    'This diagram can’t be drawn offline yet. It will appear when you’re back online.',
  'share.expires': 'The link stops working on {date}.',
  'shareOpt.90d': '90 days',
  'shareOpt.anonExpiry':
    'Links made without an account always expire — at most after 90 days — because there is no account to list and revoke them from. markbird remembers the link on this device, so you can delete it sooner.',
  'shareMgr.localTitle': 'Links from this device',
  'shareMgr.localExplainer':
    'Links made here, including ones made without an account. This device holds the secret that deletes them, so keep the deletion link if you might need it from somewhere else.',
  'shareMgr.localEmpty': 'No links made on this device.',
  'shareMgr.copyDeleteLink': 'Copy deletion link',
  'sp.deleteTitle': 'Delete this shared copy',
  'sp.deleteHint': 'You opened a deletion link for this shared copy.',
  'sp.deleteNow': 'Delete this shared copy',
  'sp.deleting': 'Deleting…',
  'sp.deleted': 'The shared copy has been deleted.',
  'sp.deleteFailed': 'That deletion link is not valid for this share.',
  'sp.invalid': 'This link is invalid or has expired.',
  'sp.notAvailable': 'Not available',
  'sp.goto': 'Go to markbird',
  'sp.titlePlaceholder': 'Untitled',
  'sp.peers': '{n} editing',
  'sp.peersTitle': 'People editing this document right now',
  'sp.protectedTitle': 'Password required',
  'sp.protectedHint': 'This shared document is password-protected.',
  'sp.wrongPassword': 'Wrong password. Please try again.',
  'sp.unlock': 'Unlock',

  // Command palette
  'cmdk.placeholder': 'Type a command or search documents…',
  'cmdk.empty': 'No results.',
  'cmdk.openDoc': 'Open document',
  'cmdk.quickOpen': 'Open a document',
  'cmdk.quickOpenPlaceholder': 'Search all documents by title, alias or folder…',

  // Version history
  'history.title': 'Version history',
  'history.empty': 'No versions yet. History is captured automatically as you write.',
  'history.saveNow': 'Save current version',
  'history.restore': 'Restore this version',
  'history.restoring': 'Restoring…',
  'history.reason.auto': 'Autosave',
  'history.reason.restore': 'Before restore',
  'history.reason.remote': 'Before sync',
  'cmd.doc.history': 'Version history…',

  // Command palette groups
  'grp.Document': 'Document',
  'grp.View': 'View',
  'grp.Export': 'Export',
  'grp.Share': 'Share',
  'grp.Appearance': 'Appearance',
  'grp.Editor': 'Editor',
  'grp.Account': 'Account',

  // Commands
  'cmd.doc.new': 'New document',
  'cmd.doc.duplicate': 'Duplicate current document',
  'cmd.doc.import': 'Import Markdown file(s)…',
  'cmd.doc.openFile': 'Open file from disk…',
  'cmd.doc.saveFile': 'Save to file (disk)',
  'cmd.doc.saveFileAs': 'Save a copy to disk…',
  'cmd.doc.delete': 'Delete current document',
  'cmd.doc.emptyTrash': 'Empty trash (permanent)',
  'cmd.view.toggleMode': 'Toggle view mode (split / switch)',
  'cmd.view.switchPane': 'Switch active pane (source / preview)',
  'cmd.view.toggleSidebar': 'Toggle sidebar',
  'cmd.view.toggleOutline': 'Toggle outline (table of contents)',
  'cmd.view.focusMode': 'Toggle focus mode (zen)',
  'cmd.editor.table.insert': 'Table: insert',
  'cmd.editor.table.format': 'Table: align columns',
  'cmd.editor.table.addRowBelow': 'Table: add row below',
  'cmd.editor.table.addColumnRight': 'Table: add column right',
  'cmd.editor.table.deleteRow': 'Table: delete row',
  'cmd.editor.table.deleteColumn': 'Table: delete column',
  'cmd.editor.table.cycleAlign': 'Table: cycle column alignment',
  'table.notInTable': 'Not in a table',
  'table.notInTableBody':
    'Put the cursor in a table first. Tables need a header row and a separator line such as | --- | --- |.',
  'cmd.editor.find': 'Find & replace',
  'cmd.editor.typewriter': 'Toggle typewriter scrolling',
  'cmd.editor.toggleVim': 'Toggle Vim mode',
  'cmd.app.settings': 'Settings…',
  'cmd.app.shortcuts': 'Keyboard shortcuts',
  'cmd.export.md': 'Export as Markdown (.md)',
  'cmd.export.html': 'Export as HTML',
  'cmd.export.pdf': 'Export as PDF',
  'cmd.export.docx': 'Export as DOCX (Word)',
  'cmd.export.slides': 'Export as slides (HTML)',
  'cmd.export.copyHtml': 'Copy rendered HTML',
  'cmd.export.copyMarkdown': 'Copy Markdown',
  'cmd.share.create': 'Share read-only link',
  'cmd.share.options': 'Share with options (access + expiry)…',
  'cmd.share.createEdit': 'Share editable link',
  'cmd.share.manage': 'Manage shared links…',
  'cmd.appearance.cycleTheme': 'Cycle theme (auto / light / dark)',
  'cmd.appearance.light': 'Theme: Light',
  'cmd.appearance.dark': 'Theme: Dark',
  'cmd.appearance.system': 'Theme: System',
  'cmd.account.signOut': 'Sign out',
  'cmd.account.signIn': 'Sign in to sync…',
  'cmd.account.settings': 'Account & data (export all, delete account)…',
  'cmd.confirm.emptyTrash': 'Permanently delete all documents in the trash?',

  /* Knowledge base (vault mode) */
  'common.loading': 'Loadingâ¦',
  'tags.inline': 'Written as #tag in the text â edit it there.',
  'embed.missing': 'Cannot embed “{target}” — no such document.',
  'embed.missingSection': 'Cannot embed “{section}” — not found in that document.',
  'embed.truncated': '“{target}” is embedded further up; not repeating it here.',
  'vault.title': 'Vault',
  'vault.tags': 'Tags',
  'vault.links': 'Links',
  'vault.graph': 'Graph',
  'vault.journal': 'Journal',
  'journal.hasNote': 'note exists',
  'journal.previousMonth': 'Previous month',
  'journal.nextMonth': 'Next month',
  'journal.openMonth': 'Open the note for {month}',
  'journal.openWeek': 'Open the note for week {week}',
  'journal.week': 'Calendar week',
  'journal.weekShort': 'Wk',
  'journal.today': 'Today',
  'vault.noTags': 'No tags yet. Add them in a document’s properties, or write #tag in the text.',
  'vault.unresolved': 'Unresolved links',
  'vault.noUnresolved': 'Every [[link]] points somewhere.',
  'vault.createNote': 'Create “{title}”',
  'vault.orphans': 'Orphan documents',
  'vault.noOrphans': 'Every document is connected.',
  'graph.title': 'Link graph',
  'graph.scope': 'Graph scope',
  'graph.local': 'Around this note',
  'graph.global': 'Whole vault',
  'graph.empty': 'Nothing to draw yet — connect documents with [[links]].',
  'graph.trimmed': 'Showing the {n} best-connected documents.',
  'sidebar.showMore': 'Show more',
};
